/**
 * OpenAI (ChatGPT) Ads Pixel integration for the stand-alone contact page.
 *
 * Mirrors `metaPixel.ts`'s approach: run the `oaiq` bootstrap programmatically
 * with a per-customer pixel ID supplied via config, rather than injecting a
 * customer-supplied `<script>` snippet verbatim.
 *
 * @see https://developers.openai.com/ads/conversions-api
 */
import { hasGlobalPrivacyControlOptOut } from "./metaPixel";

const OAIQ_SDK_SRC = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

/**
 * Minimal typing for the `oaiq` function the OpenAI Pixel SDK installs on `window`.
 */
type Oaiq = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    queue?: unknown[];
    loaded?: boolean;
};

declare global {
    interface Window {
        oaiq?: Oaiq;
    }
}

/**
 * Validates that a value looks like an OpenAI Ads Pixel ID.
 *
 * OpenAI has not published a fixed ID format (unlike Meta's numeric pixel
 * IDs), so this only rejects the empty/missing cases rather than asserting a
 * shape that might not hold.
 */
export function isValidOpenAiPixelId(pixelId: unknown): pixelId is string {
    return typeof pixelId === "string" && pixelId.trim().length > 0;
}

/**
 * Loads the OpenAI Pixel SDK (idempotent) and installs the `oaiq` stub.
 *
 * Mirrors `loadFbqBase`: creates the `oaiq` command queue, then injects the
 * `oaiq.min.js` loader script. Safe to call multiple times — subsequent calls
 * are no-ops once `window.oaiq` exists.
 *
 * Returns `true` if this call installed the stub, `false` if `window.oaiq`
 * already existed (either ours from an earlier call, or a foreign pixel the
 * host page loaded itself) — callers use this to avoid re-initializing a
 * pixel they don't own.
 */
function loadOaiqBase(): boolean {
    if (window.oaiq) {
        return false;
    }

    const oaiq: Oaiq = function (...args: unknown[]) {
        if (oaiq.callMethod) {
            oaiq.callMethod(...args);
        } else {
            (oaiq.queue as unknown[]).push(args);
        }
    };

    oaiq.queue = [];
    oaiq.loaded = true;
    window.oaiq = oaiq;

    // CSP: the host's Content-Security-Policy must allow this in script-src
    // (`script-src https://bzrcdn.openai.com`) and connect-src/img-src for
    // `https://bzr.openai.com` per OpenAI's docs. contact-app's S3/CloudFront
    // deploy does not configure CSP headers in this repo today (same as the
    // Meta Pixel above it), so there is nowhere here to wire that allowance.
    const existing = document.querySelector(`script[src="${OAIQ_SDK_SRC}"]`);
    if (!existing) {
        const script = document.createElement("script");
        script.async = true;
        script.src = OAIQ_SDK_SRC;
        const firstScript = document.getElementsByTagName("script")[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            (document.head || document.documentElement).appendChild(script);
        }
    }

    return true;
}

// Tracks which pixel IDs have been initialized so we don't double-init.
const initializedPixels = new Set<string>();

/**
 * Initializes the OpenAI Ads Pixel for the given customer pixel ID.
 *
 * Privacy behavior mirrors `initMetaPixel`: honors Global Privacy Control
 * (CCPA/CPRA) by skipping the pixel entirely for opted-out visitors. OpenAI's
 * SDK is not yet documented to expose its own granular `oaiq("consent", ...)`
 * call, so this applies the same coarse skip-on-opt-out behavior as the Meta
 * Pixel rather than guessing at an unverified consent API shape.
 *
 * Unlike `initMetaPixel`, this does not fire a baseline "page view" measure
 * call after init: per OpenAI's docs, `oaiq("init", ...)` automatically fires
 * its own `page_viewed` event, so an explicit one here would double-count.
 *
 * Unlike Meta's `fbq`, OpenAI's SDK has no documented multi-pixel model, so
 * this will not call `init` on a `window.oaiq` we didn't install ourselves
 * (e.g. one a host page's own OpenAI pixel snippet already set up) - that
 * would risk reconfiguring a pixel we don't own. Same defensive posture
 * `trackOpenAiPixelEvent` already takes via its `initializedPixels.size === 0`
 * check, just applied at init time too.
 *
 * Returns `true` if the pixel was initialized (or was already initialized for
 * this ID), `false` if the ID was missing/invalid, the visitor opted out, or
 * a foreign `window.oaiq` is already present.
 */
export function initOpenAiPixel(pixelId: string | undefined): boolean {
    if (!isValidOpenAiPixelId(pixelId)) {
        return false;
    }

    if (hasGlobalPrivacyControlOptOut()) {
        return false;
    }

    const id = pixelId.trim();

    if (initializedPixels.has(id)) {
        return true;
    }

    const installedFresh = loadOaiqBase();

    if (!installedFresh && initializedPixels.size === 0) {
        // window.oaiq existed before we ever initialized anything - it's a
        // foreign pixel, not ours to re-init.
        return false;
    }

    window.oaiq?.("init", { pixelId: id });
    initializedPixels.add(id);

    return true;
}

/**
 * Tracks an OpenAI Ads Pixel event (e.g. "lead_created").
 *
 * No-op if the pixel has not been initialized or the visitor has opted out.
 *
 * Parameter order matches `trackMetaPixelEvent` (event, data, eventId) so the
 * two sibling functions, called back-to-back in `handleSubmit`, can't be
 * copy-paste-swapped into passing eventId where data is expected.
 *
 * @param eventName - OpenAI event name
 * @param data - Optional event parameters
 * @param eventId - Shared event id. When set, it is passed as `event_id` so
 *   OpenAI deduplicates this browser event against the server-side
 *   Conversions API event carrying the same id.
 */
export function trackOpenAiPixelEvent(
    eventName: string,
    data?: Record<string, unknown>,
    eventId?: string
): void {
    // Guard on initializedPixels too: window.oaiq may already exist because a
    // host page loaded its own pixel. Without an init of our own, a measure
    // call would be credited to that foreign pixel.
    if (!window.oaiq || initializedPixels.size === 0 || hasGlobalPrivacyControlOptOut()) {
        return;
    }

    if (eventId) {
        window.oaiq("measure", eventName, data || {}, { event_id: eventId });
    } else if (data) {
        window.oaiq("measure", eventName, data);
    } else {
        window.oaiq("measure", eventName);
    }
}

/**
 * Resets the in-memory init state. Intended for tests only — no-ops in
 * production.
 */
export function __resetOpenAiPixelForTests(): void {
    if (process.env.NODE_ENV === "production") {
        return;
    }
    initializedPixels.clear();
    delete window.oaiq;
}
